import axiosInstance from './axios/index';

// ─── Enums ────────────────────────────────────────────────────────────────────
/* eslint-disable no-unused-vars */

export enum ServiceRequestStatus {
  AUTHORIZED = 'authorized',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
  DISPATCHED = 'dispatched',
  INVOICED = 'invoiced',
  IN_PROGRESS = 'in_progress',
  PENDING_AUTHORIZATION = 'pending_authorization',
  PENDING_REVIEW = 'pending_review',
  REJECTED = 'rejected'
}

export enum ServiceRequestPriority {
  HIGH = 'high',
  LOW = 'low',
  MEDIUM = 'medium',
  URGENT = 'urgent'
}

export enum ServiceRequestIntakeChannel {
  CLIENT_PORTAL = 'client_portal',
  EMAIL = 'email',
  MANUAL = 'manual'
}

export enum ServiceRequestDispatchType {
  IN_PLACE = 'in_place',
  NEW_DISPATCH = 'new_dispatch'
}

// ─── Interfaces ───────────────────────────────────────────────────────────────
/* eslint-enable no-unused-vars */

export interface ServiceRequestLineItemDTO {
  description: string;
  id?: string;
  quantity: number;
  total?: number;
  unit?: string;
  unitPrice: number;
}

export interface ServiceRequestAssignmentDTO {
  clockedInAt?: string;
  clockedOutAt?: string;
  id: string;
  notes?: string;
  scheduledEnd?: string;
  scheduledStart?: string;
  technician: {
    email: string;
    firstName: string;
    id: string;
    lastName: string;
  };
}

export interface ServiceRequestDTO {
  assignments?: ServiceRequestAssignmentDTO[];
  authorizedAt?: string;
  client: {
    businessName?: string;
    email?: string;
    firstName?: string;
    id: string;
    lastName?: string;
  };
  completedAt?: string;
  createdAt: string;
  createdBy?: { firstName: string; id: string; lastName: string };
  description?: string;
  dispatchType?: ServiceRequestDispatchType;
  estimatedDurationMinutes?: number;
  id: string;
  intakeChannel: ServiceRequestIntakeChannel;
  invoiceId?: string;
  lineItems?: ServiceRequestLineItemDTO[];
  operatorNotes?: string;
  priority: ServiceRequestPriority;
  project?: { id: string; status: string };
  requestedDate?: string;
  scheduledDate?: string;
  senderEmail?: string;
  status: ServiceRequestStatus;
  title: string;
  totalAmount: number;
  updatedAt: string;
}

export interface KanbanResponse {
  columns: Record<ServiceRequestStatus, ServiceRequestDTO[]>;
  total: number;
}

export interface ServiceRequestFilterParams {
  clientId?: string;
  fromDate?: string;
  status?: ServiceRequestStatus[];
  toDate?: string;
}

export interface CreateServiceRequestPayload {
  clientId: string;
  description?: string;
  estimatedDurationMinutes?: number;
  lineItems?: ServiceRequestLineItemDTO[];
  operatorNotes?: string;
  priority?: ServiceRequestPriority;
  projectId?: string;
  requestedDate?: string;
  title: string;
}

export interface DispatchPayload {
  dispatchType: ServiceRequestDispatchType;
  notes?: string;
  scheduledEnd?: string;
  scheduledStart?: string;
  technicianIds: string[];
}

export interface WhitelistEntryDTO {
  active: boolean;
  client?: {
    businessName?: string;
    firstName?: string;
    id: string;
    lastName?: string;
  };
  clientId?: string;
  createdAt: string;
  email: string;
  id: string;
  label?: string;
}

// ─── API calls ────────────────────────────────────────────────────────────────

const getKanban = async (
  filters?: ServiceRequestFilterParams
): Promise<KanbanResponse> => {
  const response = await axiosInstance.get('/service-requests/kanban', {
    params: filters
  });
  return response.data;
};

const getById = async (id: string): Promise<ServiceRequestDTO> => {
  const response = await axiosInstance.get(`/service-requests/${id}`);
  return response.data;
};

const create = async (
  data: CreateServiceRequestPayload
): Promise<ServiceRequestDTO> => {
  const response = await axiosInstance.post('/service-requests', data);
  return response.data;
};

const update = async (
  id: string,
  data: Partial<CreateServiceRequestPayload> & {
    projectId?: string | null;
    status?: ServiceRequestStatus;
  }
): Promise<ServiceRequestDTO> => {
  const response = await axiosInstance.patch(`/service-requests/${id}`, data);
  return response.data;
};

const remove = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/service-requests/${id}`);
};

const dispatch = async (
  id: string,
  data: DispatchPayload
): Promise<ServiceRequestDTO> => {
  const response = await axiosInstance.post(
    `/service-requests/${id}/dispatch`,
    data
  );
  return response.data;
};

const authorize = async (id: string): Promise<ServiceRequestDTO> => {
  const response = await axiosInstance.post(
    `/service-requests/${id}/authorize`
  );
  return response.data;
};

const complete = async (id: string): Promise<ServiceRequestDTO> => {
  const response = await axiosInstance.post(`/service-requests/${id}/complete`);
  return response.data;
};

const cancel = async (id: string): Promise<ServiceRequestDTO> => {
  const response = await axiosInstance.post(`/service-requests/${id}/cancel`);
  return response.data;
};

const sendAuthRequest = async (id: string): Promise<ServiceRequestDTO> => {
  const response = await axiosInstance.post(
    `/service-requests/${id}/send-auth-request`
  );
  return response.data;
};

const manualAttest = async (
  id: string,
  data: { action: 'approve' | 'decline'; note: string }
): Promise<ServiceRequestDTO> => {
  const response = await axiosInstance.post(
    `/service-requests/${id}/manual-attest`,
    data
  );
  return response.data;
};

const billingDecision = async (
  id: string,
  method: 'standalone' | 'roll_into_project' | 'bill_later' | 'pool_client'
): Promise<ServiceRequestDTO> => {
  const response = await axiosInstance.post(
    `/service-requests/${id}/billing-decision`,
    { method }
  );
  return response.data;
};

const getWhitelist = async (clientId: string): Promise<WhitelistEntryDTO[]> => {
  const response = await axiosInstance.get(
    `/service-requests/whitelist/client/${clientId}`
  );
  return response.data;
};

const addWhitelistEntry = async (data: {
  clientId: string;
  email: string;
  label?: string;
}): Promise<WhitelistEntryDTO> => {
  const response = await axiosInstance.post(
    '/service-requests/whitelist',
    data
  );
  return response.data;
};

const updateWhitelistEntry = async (
  id: string,
  data: { active?: boolean; email?: string; label?: string }
): Promise<WhitelistEntryDTO> => {
  const response = await axiosInstance.patch(
    `/service-requests/whitelist/${id}`,
    data
  );
  return response.data;
};

const removeWhitelistEntry = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/service-requests/whitelist/${id}`);
};

const getMyAssignments = async (): Promise<ServiceRequestDTO[]> => {
  const response = await axiosInstance.get('/service-requests/my-assignments');
  return response.data;
};

const poolClientSRs = async (clientId: string): Promise<any> => {
  const response = await axiosInstance.post(
    `/service-requests/billing/pool-client/${clientId}`
  );
  return response.data;
};

const getSRTimesheetTasks = async (params?: {
  fromDate?: string;
  technicianId?: string;
  toDate?: string;
}): Promise<any[]> => {
  const response = await axiosInstance.get('/timesheet/sr-tasks', { params });
  return response.data;
};

const clockIn = async (id: string): Promise<any> => {
  const response = await axiosInstance.post(`/service-requests/${id}/clock-in`);
  return response.data;
};

const clockOut = async (id: string): Promise<any> => {
  const response = await axiosInstance.post(
    `/service-requests/${id}/clock-out`
  );
  return response.data;
};

const ServiceRequestApi = {
  addWhitelistEntry,
  authorize,
  billingDecision,
  cancel,
  clockIn,
  clockOut,
  complete,
  create,
  dispatch,
  getById,
  getKanban,
  getMyAssignments,
  getSRTimesheetTasks,
  getWhitelist,
  manualAttest,
  poolClientSRs,
  remove,
  removeWhitelistEntry,
  sendAuthRequest,
  update,
  updateWhitelistEntry
};

export default ServiceRequestApi;
